// Chi tiết chuyến (Mint-Mov-Phone khung 2): 7 bước GPS, bước có ảnh / số bắt buộc thì khoá nút xác nhận tới khi đủ.
import { STEPS, TRIPS, type MovTrip } from './trips';

export type ReqKind = 'photo' | 'in' | 'qr' | 'temp';
export interface Req { key: string; label: string; kind: ReqKind; ph?: string }

export const NOTES: Record<string, string> = {
  t18: 'Kho mở 07:00–16:00, xe vào cổng phụ. Chụp ảnh seal YML4428119 khi rời kho.',
  t21: 'Kho yêu cầu xe vào cổng số 2, mang theo lệnh cấp rỗng in sẵn.',
  t24: 'Chuyến cắt mooc: kéo mooc có sẵn, bỏ qua bước kho bằng nút "Bỏ qua — cắt mooc".',
  y09: 'Đã xong.', y12: 'Đã xong.', m03: 'Dự kiến.',
};
export const JOBS: Record<string, string> = {
  t18: 'SE260637420', t21: 'HANA-SE-2610-0142', t24: 'HANA-SE-2610-0139', y09: 'HANA-SE-2610-0127', y12: 'HANA-SE-2610-0109', m03: 'HANA-SE-2610-0130',
};
/** Giờ bước đã xong (GPS) theo dữ liệu mẫu board. */
export const TIMES: Record<string, readonly string[]> = { t18: ['05:58', '06:41'] };

/** Cho phép lệch ±2°C so với nhiệt độ yêu cầu; quá thì báo điều độ và CS (board: `dev > 2`). */
export const RF_TOL = 2;
/** Nhiệt độ AI "đọc" từ ảnh màn máy lạnh khi chưa nhập tay (board: `-18,4`). */
export const RF_READ = '-18,4';

/** Yêu cầu bắt buộc của bước i (board: reqDefs). `rf` = nhiệt độ yêu cầu của cont lạnh, có thì thêm mục máy lạnh ở bước 1 · 3 · 5. */
export function reqsOf(i: number, rf?: number): Req[] {
  const temp = (key: string, label: string): Req[] => (rf === undefined ? [] : [{ key, label, kind: 'temp' }]);
  if (i === 1) return [{ key: 'cont', label: 'Số cont', kind: 'in', ph: 'VD: ONEU 812337-4' }, { key: 'qr', label: 'Quét QR mooc', kind: 'qr' }, { key: 'eir1', label: 'Ảnh EIR lấy rỗng', kind: 'photo' }, ...temp('rf1', 'Máy lạnh lúc nhận cont')];
  if (i === 3) return [{ key: 'seal', label: 'Số seal', kind: 'in', ph: 'VD: ONE5521873' }, { key: 'sealp', label: 'Ảnh cont đã niêm chì', kind: 'photo' }, ...temp('rf3', 'Máy lạnh lúc rời kho')];
  if (i === 5) return [{ key: 'eir2', label: 'Ảnh EIR hạ cont', kind: 'photo' }, ...temp('rf5', 'Máy lạnh lúc hạ cont')];
  if (i === 6) return [{ key: 'epod', label: 'ePOD · biên bản giao nhận có ký', kind: 'photo' }];
  return [];
}

type Vals = Record<string, string | boolean>;

/** Số đọc được từ ô nhiệt độ ("-18,4", "−18.4"); không hợp lệ → null. */
export function parseTemp(v: string | boolean | undefined): number | null {
  const s = String(v ?? '').trim().replace(',', '.').replace('−', '-');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Độ lệch tuyệt đối so với nhiệt độ yêu cầu; chưa có số → null. */
export function tempDev(v: string | boolean | undefined, rf: number): number | null {
  const n = parseTemp(v);
  return n === null ? null : Math.abs(n - rf);
}

export function reqDone(r: Req, v: string | boolean | undefined, vals: Vals = {}): boolean {
  if (r.kind === 'temp') return !!vals[r.key + 'p'] && parseTemp(v) !== null;
  return r.kind === 'in' ? String(v ?? '').trim().length >= 4 : !!v;
}

export function missing(i: number, vals: Vals, rf?: number): string[] {
  return reqsOf(i, rf).filter((r) => !reqDone(r, vals[r.key], vals)).map((r) => r.label);
}

/** Các mục máy lạnh của bước i đã nhập mà lệch quá ±2°C — xác nhận vẫn được, hệ thống báo điều độ và CS. */
export function tempAlerts(i: number, vals: Vals, rf?: number): string[] {
  if (rf === undefined) return [];
  return reqsOf(i, rf).filter((r) => r.kind === 'temp' && (tempDev(vals[r.key], rf) ?? 0) > RF_TOL).map((r) => r.label);
}

/** "2,3" kiểu Việt. */
export const fmtDev = (d: number) => d.toFixed(1).replace('.', ',');

export function confirmLabel(i: number, tripId: string): string {
  if (tripId === 't24' && (i === 2 || i === 3)) return 'Bỏ qua — cắt mooc';
  return i === 6 ? 'Kết thúc chuyến' : `Xác nhận: ${STEPS[i]}`;
}

export function gpsLine(i: number, tripId: string): string {
  if (tripId === 't24' && (i === 2 || i === 3)) return 'Chuyến cắt mooc: bước này không áp dụng';
  if (tripId === 't18' && i === 2) return 'GPS: xe đã vào vùng Kho Minh Hải lúc 09:24 — bấm xác nhận khi đã vào cổng';
  return 'Chờ GPS — đến nơi có thể bấm xác nhận tay';
}

export function findTrip(id: string | null): MovTrip | undefined {
  const all = [...TRIPS.yest, ...TRIPS.today, ...TRIPS.tmr];
  return all.find((t) => t.id === id) ?? TRIPS.today[0];
}

export const mapUrl = (q: string) => 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
